import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert, AlertTitle, Box, Button, Chip, CircularProgress, FormControlLabel, MenuItem, Paper, Select, Switch,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import audioSettingsStore from '../../Mobx/AudioSettingsStore';
import {
    DICTIONARY_INFO, INWORLD_KEY_NOTE, LANGUAGE_INFO, LANGUAGE_KEYS, RULE_PROVIDER_INWORLD, formatError
} from '../../Mobx/AudioCatalog';
import {
    autoPatchPaths, buildAutoPatch, dictionaryKeysOf, fieldLabelOf, formFromAutoView, isAutoFormChanged,
    isProviderUnavailable, providersOf, summarizeRules, validateAutoForm
} from './autoRulesForm';
import AudioFailures from './AudioFailures';
import useServerForm from './useServerForm';

/**
 * The rules of the automatic creation of the audio files: for each dictionary and each language - whether the audio is created
 * when an entry is added or changed, and by which provider (Google or Inworld).
 * What is saved here is what the server uses every time an entry gets its audio files (ai_settings.audio.speech.auto).
 */

const SAVED_MESSAGE_MS = 6000;
const CHANGED_ROW_COLOR = '#fff8e1';

const AutoRulesSettings = observer(() => {
    const store = audioSettingsStore;
    const { view, loading, loadError, loadCount, savingAuto, saveAutoError } = store;
    const auto = store.auto;
    // The form starts from the rules that the server really uses, and starts again after its own save or a reload
    const [form, setForm] = useServerForm(auto, formFromAutoView, loadCount);
    const [savedVisible, setSavedVisible] = useState(false);

    useEffect(() => {
        store.load();
    }, [store]);

    useEffect(() => {
        if (!savedVisible) return undefined;
        const timer = setTimeout(() => setSavedVisible(false), SAVED_MESSAGE_MS);
        return () => clearTimeout(timer);
    }, [savedVisible]);

    const patch = useMemo(() => (auto && form ? buildAutoPatch(form, auto) : {}), [auto, form]);
    const problems = useMemo(() => (view && auto && form ? validateAutoForm(form, view) : []), [view, auto, form]);

    if (!view) {
        if (loadError) {
            return (
                <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
                    <Alert
                        severity="error"
                        action={<Button color="inherit" size="small" onClick={store.load} disabled={loading}>נסו שוב</Button>}
                    >
                        <AlertTitle>טעינת ההגדרות נכשלה</AlertTitle>
                        <Box dir="auto">{formatError(loadError)}</Box>
                    </Alert>
                </Box>
            );
        }
        return (
            <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!auto || !form) {
        return (
            <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
                <Alert severity="warning" action={<Button color="inherit" size="small" onClick={store.load} disabled={loading}>טעינה מחדש</Button>}>
                    <AlertTitle>השרת עדיין לא עודכן</AlertTitle>
                    התשובה של GET /api/ai/settings/audio אינה כוללת את הכללים של היצירה האוטומטית (החלק auto), ולכן אי אפשר להציג או לשמור אותם.
                </Alert>
            </Box>
        );
    }

    const { effective, catalog } = auto;
    const { capabilities } = view;
    const providers = providersOf(auto);
    const inworldKeyMissing = capabilities?.inworldApiKey === false;
    const paths = autoPatchPaths(patch);
    const canSave = paths.length > 0 && problems.length === 0 && !savingAuto;
    const summary = summarizeRules(form);

    const setCell = (dictionary, language, field, value) => setForm((current) => ({
        ...current,
        rules: {
            ...current.rules,
            [dictionary]: {
                ...current.rules[dictionary],
                [language]: { ...current.rules[dictionary][language], [field]: value },
            },
        },
    }));

    const handleSave = async () => {
        if (await store.saveAuto(patch)) setSavedVisible(true);
    };

    const providerSummary = providers
        .map((provider) => `${provider.label}: ${summary.byProvider[provider.id] || 0}`)
        .join(', ');

    return (
        <Box dir="rtl" sx={{ p: 2, maxWidth: 1100, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>כללי יצירה אוטומטית של אודיו</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                כשמוסיפים ערך למילון או משנים את הטקסט הנאמר בו, המערכת יוצרת אוטומטית את קובצי האודיו שלו (קול נשי וקול גברי).
                הכללים קובעים לכל מילון ולכל שפה אם ליצור, ובאיזה ספק. הכלל שחל הוא של המילון שבו בוצע השינוי.
                ההגדרות של כל ספק (מודל, קולות, מהירות) נמצאות בלשוניות "הגדרות Google" ו"הגדרות Inworld".
            </Typography>

            {/* __________ מצב נוכחי __________ */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    <Chip
                        size="small"
                        color={effective.enabled ? 'success' : 'default'}
                        label={`יצירה אוטומטית: ${effective.enabled ? 'פעילה' : 'כבויה'}`}
                    />
                    <Chip
                        size="small"
                        color={capabilities?.geminiApiKey === false ? 'error' : 'success'}
                        label={`מפתח Google (Gemini): ${capabilities?.geminiApiKey === false ? 'חסר' : 'קיים'}`}
                    />
                    <Chip
                        size="small"
                        color={inworldKeyMissing ? 'warning' : 'success'}
                        label={`מפתח Inworld בשרת: ${inworldKeyMissing ? 'חסר' : 'קיים'}`}
                    />
                    <Chip size="small" variant="outlined" label={`תאים פעילים: ${summary.enabled} מתוך ${summary.total}`} />
                    {summary.enabled > 0 && <Chip size="small" variant="outlined" label={providerSummary} />}
                </Box>
                {inworldKeyMissing && <Alert severity="info" sx={{ mb: 1.5 }}>{INWORLD_KEY_NOTE} עד אז אפשר לשמור כללים רק עם Google.</Alert>}
                {effective.fellBack ? (
                    <Alert severity="warning">
                        <AlertTitle>חלק מהכללים השמורים במסד הנתונים אינם תקינים</AlertTitle>
                        כל עוד זה כך, היצירה האוטומטית משתמשת בכלל ברירת המחדל במקומם.
                        לחצו על "שמירה" כדי להחליף את הערכים הלא תקינים בערכים המוצגים למטה.
                        <Box component="ul" dir="ltr" sx={{ mt: 1, mb: 0, paddingInlineStart: '24px', fontSize: 13 }}>
                            {effective.warnings.map((warning) => <li key={warning}>{warning}</li>)}
                        </Box>
                    </Alert>
                ) : (
                    <Alert severity="success">
                        הכללים תקינים. כל יצירה אוטומטית חדשה של אודיו פועלת לפיהם מיד. ברירת המחדל שלפני שהוגדר משהו: עברית ואנגלית עם Google, וערבית כבויה.
                    </Alert>
                )}
            </Paper>

            {/* __________ מתג כללי __________ */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <FormControlLabel
                    control={<Switch checked={form.enabled} onChange={(e) => setForm((current) => ({ ...current, enabled: e.target.checked }))} />}
                    label="יצירה אוטומטית פעילה"
                />
                <Typography variant="body2" color="text.secondary">
                    כשהמתג כבוי לא נוצר אודיו אוטומטית בשום מילון, בלי קשר לכללים שלמטה. קבצים קיימים לא משתנים, ומחיקת אודיו של ערך שהוסר ממשיכה לפעול כרגיל.
                </Typography>
            </Paper>

            {/* __________ כללים לפי מילון ושפה __________ */}
            {dictionaryKeysOf(auto).map((dictionary) => {
                const dictionaryCatalog = catalog.dictionaries?.find((entry) => entry.key === dictionary);
                const dictionaryLabel = DICTIONARY_INFO[dictionary]?.label || dictionaryCatalog?.label || dictionary;
                return (
                    <Paper key={dictionary} variant="outlined" sx={{ p: 2, mb: 2, opacity: form.enabled ? 1 : 0.6 }}>
                        <Typography variant="h6" gutterBottom>{dictionaryLabel}</Typography>
                        <TableContainer>
                            <Table size="small" aria-label={dictionaryLabel}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 'bold' }}>שפה</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>השדה שנוצר ממנו אודיו</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold' }}>ליצור</TableCell>
                                        <TableCell sx={{ fontWeight: 'bold', minWidth: 190 }}>ספק</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {LANGUAGE_KEYS.map((language) => {
                                        const cell = form.rules[dictionary][language];
                                        const current = effective.rules?.[dictionary]?.[language] || {};
                                        const changed = cell.enabled !== current.enabled || cell.provider !== current.provider;
                                        const label = `${dictionaryLabel}, ${LANGUAGE_INFO[language].label}`;
                                        return (
                                            <TableRow key={language} sx={{ bgcolor: changed ? CHANGED_ROW_COLOR : undefined }}>
                                                <TableCell sx={{ fontWeight: 'bold' }}>{LANGUAGE_INFO[language].label}</TableCell>
                                                <TableCell>{fieldLabelOf(dictionaryCatalog, language)}</TableCell>
                                                <TableCell>
                                                    <Switch
                                                        checked={cell.enabled}
                                                        onChange={(e) => setCell(dictionary, language, 'enabled', e.target.checked)}
                                                        inputProps={{ 'aria-label': `${label}: ליצור אודיו אוטומטית` }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    <Select
                                                        size="small"
                                                        fullWidth
                                                        value={providers.some((provider) => provider.id === cell.provider) ? cell.provider : ''}
                                                        onChange={(e) => setCell(dictionary, language, 'provider', e.target.value)}
                                                        inputProps={{ 'aria-label': `${label}: ספק` }}
                                                    >
                                                        {providers.map((provider) => {
                                                            const unavailable = isProviderUnavailable(provider.id, capabilities, current.provider);
                                                            return (
                                                                <MenuItem key={provider.id} value={provider.id} disabled={unavailable}>
                                                                    <bdi>{provider.label}</bdi>{unavailable ? ' - לשרת אין מפתח' : ''}
                                                                </MenuItem>
                                                            );
                                                        })}
                                                    </Select>
                                                    {cell.provider === RULE_PROVIDER_INWORLD && inworldKeyMissing && (
                                                        <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                                                            לשרת אין מפתח Inworld: היצירה תיכשל עד שיתווסף.
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                );
            })}

            {/* __________ שמירה __________ */}
            <Paper
                elevation={3}
                sx={{ p: 2, mb: 2, position: 'sticky', bottom: 8, zIndex: 2, display: 'flex', flexDirection: 'column', gap: 1 }}
            >
                {problems.length > 0 && (
                    <Alert severity="error">
                        <Box component="ul" sx={{ m: 0, paddingInlineStart: '24px' }}>
                            {problems.map((problem) => <li key={problem}>{problem}</li>)}
                        </Box>
                    </Alert>
                )}
                {saveAutoError && (
                    <Alert severity="error">
                        <AlertTitle>השמירה נכשלה</AlertTitle>
                        <Box dir="auto">{formatError(saveAutoError)}</Box>
                    </Alert>
                )}
                {savedVisible && (
                    <Alert severity="success" onClose={() => setSavedVisible(false)}>
                        נשמר. הכללים החדשים חלים מיד על כל יצירה אוטומטית חדשה.
                    </Alert>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Button variant="contained" onClick={handleSave} disabled={!canSave}>
                        {savingAuto ? 'שומר...' : 'שמירה'}
                    </Button>
                    <Button variant="outlined" onClick={() => setForm(formFromAutoView(auto))} disabled={savingAuto || !isAutoFormChanged(form, auto)}>
                        ביטול שינויים
                    </Button>
                    <Button variant="text" onClick={store.load} disabled={loading || savingAuto}>
                        טעינה מחדש מהשרת
                    </Button>
                    <Typography variant="body2" color="text.secondary" dir="auto">
                        {paths.length > 0 ? `יישמרו: ${paths.join(', ')}` : 'אין מה לשמור'}
                    </Typography>
                </Box>
            </Paper>

            {/* __________ כשלים __________ */}
            <AudioFailures />

            {/* __________ פרטים טכניים __________ */}
            <Box component="details" sx={{ mt: 2, mb: 4 }}>
                <Box component="summary" sx={{ cursor: 'pointer', color: 'text.secondary' }}>פרטים טכניים: מה שמור כרגע במסד הנתונים</Box>
                <Box component="pre" dir="ltr" sx={{ fontSize: 12, overflow: 'auto', bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                    {JSON.stringify(auto.configured, null, 2)}
                </Box>
            </Box>
        </Box>
    );
});

export default AutoRulesSettings;
